/**
 * The JARVIS conversation surface, shared by the dock panel and the /jarvis
 * page: conversation switcher, streaming transcript, HUD status line, action
 * confirmation cards, "Remembered" memory chips with Undo, suggestion chips,
 * push-to-talk and read-aloud.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useJarvis, checkStatus, sendMessage, stopStreaming, newConversation, openConversation, deleteConversation,
  confirmAction, rejectAction, setSpeaker, setListening, undoMemory,
} from './jarvisStore';
import { useSpeechRecognition, synthesisSupported } from './voice';
import SafeMarkdown from './SafeMarkdown';
import ArcReactor from './ArcReactor';
import './jarvis.css';

const SUGGESTIONS = [
  { label: 'Morning briefing', text: 'Give me my morning briefing.' },
  { label: 'Who should I call today?', text: 'Who should I call today, and why?' },
  { label: 'Big low bids this week', text: 'Show me the big apparent low bids from the last 7 days on prospects.' },
  { label: 'Draft a bond kit email to…', text: 'Draft a bond kit email to ', fill: true },
  { label: 'Note an enhancement', text: 'I have an idea for BondHQ: ', fill: true },
  { label: "What's new in BondHQ?", text: "What's new in BondHQ lately?" },
];

const GITHUB_URL = /^https:\/\/github\.com\//;

const STATUS_LABEL = { pending: 'Awaiting approval', running: 'Applying', done: 'Done', rejected: 'Rejected', expired: 'Expired', failed: 'Failed' };

function resultLine(a) {
  if (a.status === 'done') {
    const r = a.result || {};
    if (r.message) return r.message;
    if (r.to_stage) return `Moved to ${r.to_stage}${r.owner ? ` · owner ${r.owner}` : ''}.`;
    if (r.follow_up_id) return `Follow-up #${r.follow_up_id} created.`;
    if (r.contact_id) return `Contact #${r.contact_id} added.`;
    if (r.updated) return `Updated ${r.updated.join(', ')}.`;
    if (r.outreach_log_id || r.completed_follow_up_id) return 'Logged.';
    return 'Applied.';
  }
  if (a.status === 'failed') return (a.result && a.result.error) || a.error || 'The change could not be applied.';
  return null;
}

function ActionCard({ action }) {
  const pending = action.status === 'pending';
  const line = resultLine(action);
  return (
    <div className={`jv-action jv-action--${action.status}`} role="group" aria-label={`Proposed action ${action.id}`}>
      <div className="jv-action-head">
        <span className="jv-micro">Action #{action.id} · {String(action.tool || '').replace(/_/g, ' ')}</span>
        <span className={`jv-badge jv-badge--${action.status}`}>{STATUS_LABEL[action.status] || action.status}</span>
      </div>
      <p className="jv-action-summary">{action.summary}</p>
      {action.tool === 'note_enhancement' && action.payload && <EnhancementDetails p={action.payload} />}
      {line && <p className="jv-action-result">{line}</p>}
      {action.result && action.result.github_url && GITHUB_URL.test(action.result.github_url) && (
        <p className="jv-action-result">
          <a className="jv-link" href={action.result.github_url} target="_blank" rel="noopener noreferrer">
            Open GitHub issue #{action.result.github_issue_number}
          </a>
        </p>
      )}
      {action.error && action.status !== 'failed' && <p className="jv-action-error" role="alert">{action.error}</p>}
      {pending && (
        <div className="jv-action-buttons">
          <button type="button" className="jv-btn jv-btn--approve" disabled={action.busy} onClick={() => confirmAction(action.id)}>
            {action.busy ? 'Applying…' : 'Approve'}
          </button>
          <button type="button" className="jv-btn jv-btn--ghost" disabled={action.busy} onClick={() => rejectAction(action.id)}>
            Reject
          </button>
        </div>
      )}
    </div>
  );
}

/** The enhancement card body: title, kind, priority and acceptance criteria. */
function EnhancementDetails({ p }) {
  const criteria = Array.isArray(p.acceptance_criteria) ? p.acceptance_criteria : [];
  return (
    <div className="jv-enh">
      <div className="jv-enh-title">{p.title}</div>
      <div className="jv-enh-meta">
        <span className={`jv-badge jv-badge--kind-${p.kind === 'bug' ? 'bug' : 'enh'}`}>{p.kind === 'bug' ? 'Bug' : 'Enhancement'}</span>
        <span className={`jv-badge jv-badge--prio-${p.priority}`}>{p.priority} priority</span>
      </div>
      {criteria.length > 0 && (
        <>
          <div className="jv-micro">Acceptance criteria</div>
          <ul className="jv-enh-criteria">
            {criteria.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </>
      )}
    </div>
  );
}

/** "Remembered: …" with Undo (live) — or a plain note when reloaded from history. */
function MemoryChip({ item }) {
  const m = item.memory;
  const verb = item.forgotten ? 'Forgot' : item.undone ? 'Undone' : m && m.updated ? 'Memory updated' : 'Remembered';
  return (
    <div className={`jv-memory${item.undone ? ' is-undone' : ''}`} role="status">
      <span className="jv-memory-icon" aria-hidden="true">◆</span>
      <span className="jv-memory-text"><span className="jv-memory-verb">{verb}:</span> {item.text}</span>
      {m && !item.forgotten && !item.undone && (
        <button type="button" className="jv-memory-undo" disabled={item.busy} onClick={() => undoMemory(item.key)}>
          {item.busy ? 'Undoing…' : 'Undo'}
        </button>
      )}
      {item.error && <span className="jv-memory-error" role="alert">{item.error}</span>}
    </div>
  );
}

export default function JarvisChat({ variant = 'dock', onClose, autoFocus = false }) {
  const s = useJarvis();
  const navigate = useNavigate();
  const location = useLocation();
  const [draft, setDraft] = useState('');
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { if (s.status === 'unknown') checkStatus(); }, [s.status]);
  useEffect(() => { if (autoFocus && inputRef.current) inputRef.current.focus(); }, [autoFocus]);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [s.items, s.hud]);

  const page = location.pathname + (location.search || '');
  const send = (text) => {
    const t = String(text || '').trim();
    if (!t) return;
    setDraft('');
    sendMessage(t, { page: page.slice(0, 200) });
  };

  const voice = useSpeechRecognition({
    onStart: () => setListening(true),
    onEnd: () => setListening(false),
    onFinal: (t) => send(t),
  });

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(draft); }
  };

  const go = (path) => {
    navigate(path);
    if (variant === 'dock' && onClose && window.matchMedia && window.matchMedia('(max-width: 640px)').matches) onClose();
  };

  const mode = s.status === 'offline' ? 'offline' : s.listening ? 'listening' : s.speaking ? 'speaking' : s.streaming ? 'thinking' : 'idle';
  const online = s.status === 'online';
  const empty = !s.items.length && !s.loadingConversation;

  return (
    <section className={`jv-chat jv-chat--${variant}`} aria-label="JARVIS assistant">
      <header className="jv-chat-header">
        <div className="jv-chat-title">
          {variant === 'dock' && <ArcReactor mode={mode} size="xs" />}
          <div>
            <div className="jv-title">J.A.R.V.I.S.</div>
            <div className="jv-micro" aria-live="polite">
              {s.status === 'offline' ? 'Offline' : s.status === 'forbidden' ? 'Unavailable' : s.streaming ? 'Engaged' : online ? 'Online' : 'Connecting'}
            </div>
          </div>
        </div>
        <div className="jv-chat-tools">
          {online && (
            <>
              <button type="button" className="jv-icon-btn" onClick={() => go('/jarvis/memory')} title="What JARVIS remembers about you" aria-label="Open JARVIS memory">◆</button>
              <label className="jv-sr-only" htmlFor={`jv-conv-${variant}`}>Conversation</label>
              <select
                id={`jv-conv-${variant}`}
                className="jv-select"
                value={s.currentId || ''}
                disabled={s.streaming}
                onChange={(e) => (e.target.value ? openConversation(Number(e.target.value)) : newConversation())}
              >
                <option value="">New conversation</option>
                {s.conversations.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
              <button type="button" className="jv-icon-btn" onClick={newConversation} disabled={s.streaming} title="New conversation" aria-label="New conversation">＋</button>
              {s.currentId && (
                <button type="button" className="jv-icon-btn" disabled={s.streaming} title="Delete conversation" aria-label="Delete conversation"
                  onClick={() => { if (window.confirm('Delete this conversation?')) deleteConversation(s.currentId); }}>⌫</button>
              )}
            </>
          )}
          {synthesisSupported() && (
            <button type="button" className={`jv-icon-btn${s.speakerOn ? ' is-on' : ''}`} aria-pressed={s.speakerOn}
              title={s.speakerOn ? 'Read replies aloud: on' : 'Read replies aloud: off'} aria-label="Read replies aloud"
              onClick={() => setSpeaker(!s.speakerOn)}>{s.speakerOn ? '🔊' : '🔈'}</button>
          )}
          {onClose && (
            <button type="button" className="jv-icon-btn" onClick={onClose} title="Close (Esc)" aria-label="Close JARVIS">✕</button>
          )}
        </div>
      </header>

      <div className="jv-transcript" ref={scrollRef} aria-live="polite" aria-busy={s.streaming}>
        {s.status === 'offline' && (
          <div className="jv-offline">
            <ArcReactor mode="offline" size="sm" />
            <p className="jv-offline-title">JARVIS is offline.</p>
            <p className="jv-dim">An administrator needs to set <code>ANTHROPIC_API_KEY</code> on the server.</p>
          </div>
        )}
        {s.status === 'forbidden' && <p className="jv-dim jv-pad">JARVIS isn't available on this account.</p>}
        {s.status === 'error' && (
          <div className="jv-pad">
            <p className="jv-dim">Couldn't reach JARVIS.</p>
            <button type="button" className="jv-btn jv-btn--ghost" onClick={checkStatus}>Retry</button>
          </div>
        )}
        {online && empty && (
          <div className="jv-empty">
            {variant === 'dock' && <ArcReactor mode={mode} size="sm" />}
            <p className="jv-empty-title">At your service.</p>
            <p className="jv-dim">Ask about contractors, lettings, bid results or your pipeline. I can propose updates — you approve them.</p>
            <div className="jv-chips">
              {SUGGESTIONS.map((sg) => (
                <button key={sg.label} type="button" className="jv-chip"
                  onClick={() => { if (sg.fill) { setDraft(sg.text); if (inputRef.current) inputRef.current.focus(); } else send(sg.text); }}>
                  {sg.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {s.loadingConversation && <p className="jv-dim jv-pad">Loading conversation…</p>}
        {s.items.map((it) => {
          if (it.kind === 'user') return <div key={it.key} className="jv-msg jv-msg--user"><p>{it.text}</p></div>;
          if (it.kind === 'assistant') {
            return (
              <div key={it.key} className={`jv-msg jv-msg--assistant${it.live ? ' is-live' : ''}`}>
                <SafeMarkdown text={it.text} onNavigate={go} />
                {it.live && <span className="jv-caret" aria-hidden="true" />}
              </div>
            );
          }
          if (it.kind === 'tool') return <div key={it.key} className="jv-toolline">◢ {String(it.label || it.name).toUpperCase()}</div>;
          if (it.kind === 'action') return <ActionCard key={it.key} action={it.action} />;
          if (it.kind === 'memory') return <MemoryChip key={it.key} item={it} />;
          if (it.kind === 'error') return <div key={it.key} className="jv-msg jv-msg--error" role="alert">{it.text}</div>;
          return null;
        })}
      </div>

      {s.streaming && s.hud && (
        <div className="jv-hud" role="status">
          <span className="jv-hud-pulse" aria-hidden="true" />◢ {String(s.hud).toUpperCase()}…
        </div>
      )}
      {voice.active && <div className="jv-interim" aria-live="polite">{voice.interim || 'Listening…'}</div>}
      {voice.error && !voice.active && <div className="jv-interim jv-interim--error" role="alert">{voice.error}</div>}

      {online && (
        <form className="jv-composer" onSubmit={(e) => { e.preventDefault(); send(draft); }}>
          <label className="jv-sr-only" htmlFor={`jv-input-${variant}`}>Message JARVIS</label>
          <textarea
            id={`jv-input-${variant}`}
            ref={inputRef}
            className="jv-input"
            rows={variant === 'page' ? 2 : 1}
            maxLength={4000}
            placeholder={voice.supported ? 'Ask JARVIS… or hold the mic' : 'Ask JARVIS…'}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
          />
          <button
            type="button"
            className={`jv-icon-btn jv-mic${voice.active ? ' is-on' : ''}`}
            disabled={!voice.supported || s.streaming}
            aria-pressed={voice.active}
            aria-label={voice.supported ? 'Hold to talk' : 'Voice input is not supported in this browser'}
            title={voice.supported ? 'Hold to talk (Space/Enter toggles)' : 'Voice input is not supported in this browser (try Chrome or Edge)'}
            onPointerDown={(e) => { if (e.button === 0 || e.pointerType !== 'mouse') voice.start(); }}
            onPointerUp={voice.stop}
            onPointerLeave={() => { if (voice.active) voice.stop(); }}
            onKeyDown={(e) => {
              if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); if (!e.repeat) { if (voice.active) voice.stop(); else voice.start(); } }
            }}
          >🎙</button>
          {s.streaming ? (
            <button type="button" className="jv-btn jv-btn--ghost" onClick={stopStreaming}>Stop</button>
          ) : (
            <button type="submit" className="jv-btn jv-btn--send" disabled={!draft.trim()}>Send</button>
          )}
        </form>
      )}
    </section>
  );
}
